import React, {useCallback, useEffect, useMemo, useState} from "react";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import {ToastContainer} from "react-toastify";
import {Calendar} from "primereact/calendar";
import {Checkbox} from "primereact/checkbox";
import {Button} from "primereact/button";
import {FloatLabel} from "primereact/floatlabel";
import {Toolbar} from "primereact/toolbar";
import {Card} from "primereact/card";


import {axiosInstance} from "../utilities/AxiosConfig";
import {notify} from "../components/toast";
import {MunshiMenu} from "../components/Menu";
import RMAGrid from "../components/RMA/RMAGrid";
import {generateRandomString} from "../utilities/randomGenerator";
import {Auths} from "../utilities/getAuthDep";
import {RmaPrint} from "../print/RmaPrint";
import {useSpinner} from "../context/SpinnerContext";

const createEmptyRmaRow = () => ({
    id: generateRandomString(4),
    date: dayjs().format("YYYY-MM-DD"),
    bookId: 0,
    customer: "",
    description: "",
    technician: "",
    warranty: false,
    finished: false,
    diagnostic: "",
    delivered: false,
    deliveryDate: dayjs().format("YYYY-MM-DD"),
    quantity: 0,
    repeat: false,
    repeatCount: 0,
    completionDate: dayjs().format("YYYY-MM-DD"),
    expenses: 0,
    charges: 0,
    comment: "",
});

const FILTERS = [
    {key: "Finished", label: "Finished"},
    {key: "Delivered", label: "Delivered"},
    {key: "Charges", label: "Charges"},
    {key: "Expenses", label: "Expenses"},
    {key: "Warranty", label: "Warranty"},
    {key: "NoWarranty", label: "No Warranty"},
];

export const Rma = ({handle}) => {
    const [data, setData] = useState(null);
    const [techList, setTechList] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const {loading, setLoading} = useSpinner();

    // Keeping these since your app likely uses them elsewhere; not used in this component yet.
    const [level] = useState(Auths);
    const [user, setUser] = useState("Guest");
    const [database, setDatabase] = useState("Test");

    useEffect(() => {
        setUser(Cookies.get("user") || "Guest");
        setDatabase(Cookies.get("database") || "Test");
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                const techResponse = await axiosInstance.get("tech/");
                const technicians = Array.isArray(techResponse?.data)
                    ? techResponse.data
                        .map((it) => {
                            const first = (it?.firstName ?? "").toString().trim();
                            const last = (it?.lastName ?? "").toString().trim();
                            return `${first} ${last}`.trim();
                        })
                        .filter(Boolean)
                    : [];
                setTechList(technicians);

                const response = await axiosInstance.get("/rma/");
                const rows = Array.isArray(response?.data) && response.data.length > 0
                    ? response.data
                    : [createEmptyRmaRow()];
                setData(rows);
            } catch (error) {
                notify(error?.message || "Failed to load RMA data", "error");
                setData([createEmptyRmaRow()]);
                setTechList([]);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [setLoading]);

    const deliveredRows = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data.filter((it) => !it?.delivered);
    }, [data]);

    const handleAction = useCallback(
        async ({mode, _data}) => {
            if (!_data) return;

            try {
                setLoading(true);

                if (mode === "Save") {
                    if (!_data.customer?.trim() || !_data.description?.trim()) {
                        notify("Customer and Description are required.", "error");
                        return;
                    }

                    const res = await axiosInstance.post("rma/", _data);

                    if (res.data > 0) {
                        // If backend returns new numeric id, replace temp id.
                        if (!Number.isInteger(_data.id) || _data.id === 0) {
                            const newId = res.data;
                            setData((prev) =>
                                Array.isArray(prev)
                                    ? prev.map((it) => {
                                        if (it === _data) return {...it, id: newId};
                                        if (it?.id === 0 || !Number.isInteger(it?.id)) return {...it, id: newId};
                                        return it;
                                    })
                                    : prev
                            );
                        }
                        notify("Successfully saved", "success");
                    } else {
                        notify("Failed to save", "error");
                    }

                    return;
                }

                // Default = Delete
                const id = _data.id;
                if (!id) return;

                const response = await axiosInstance.delete(`rma/${id}`);
                if (response.data > 0) {
                    setData((prev) => (Array.isArray(prev) ? prev.filter((it) => it?.id !== id) : prev));
                    notify("Successfully deleted", "success");
                } else {
                    notify("Failed to delete", "error");
                }
            } catch (error) {
                notify(error?.message || "Action failed", "error");
            } finally {
                setLoading(false);
            }
        },
        [setLoading]
    );

    const PrintTools = () => {
        const [dates, setDates] = useState([new Date(), new Date()]);
        const [selectedFilters, setSelectedFilters] = useState([]);

        const toggleFilter = (e) => {
            const key = e.value;
            setSelectedFilters((prev) => {
                const next = new Set(prev);
                if (e.checked) next.add(key);
                else next.delete(key);
                return Array.from(next);
            });
        };

        const applyFilters = (rows, rangeDates, filters) => {
            let out = Array.isArray(rows) ? [...rows] : [];

            if (Array.isArray(rangeDates) && rangeDates[0] && rangeDates[1]) {
                const startTs = dayjs(rangeDates[0]).startOf("day").valueOf();
                const endTs = dayjs(rangeDates[1]).endOf("day").valueOf();
                out = out.filter((it) => {
                    if (!it?.date) return false;
                    const ts = dayjs(it.date).valueOf();
                    if (!Number.isFinite(ts)) return false;
                    return ts >= startTs && ts <= endTs;
                });
            }

            for (const f of filters) {
                switch (f) {
                    case "Finished":
                        out = out.filter((it) => Boolean(it?.finished));
                        break;
                    case "Delivered":
                        out = out.filter((it) => Boolean(it?.delivered));
                        break;
                    case "Charges":
                        out = out.filter((it) => Number(it?.charges) > 0);
                        break;
                    case "Warranty":
                        out = out.filter((it) => Boolean(it?.warranty));
                        break;
                    case "Expenses":
                        out = out.filter((it) => Number(it?.expenses) > 0);
                        break;
                    case "NoWarranty":
                        out = out.filter((it) => !it?.warranty);
                        break;
                    default:
                        break;
                }
            }

            return out;
        };

        const handlePrint = () => {
            const rowsToPrint = applyFilters(data, dates, selectedFilters);
            RmaPrint(rowsToPrint, user || "Guest");
        };

        return (
            <Card
                className="w-full"
                style={{
                    borderRadius: "10px",
                    backgroundColor: "#70716E",
                    border: "1px solid rgba(255,255,255,0.10)",
                    boxShadow:
                        "inset 2px 2px 6px rgba(255,255,255,0.22), inset -2px -2px 6px rgba(0,0,0,0.35), 0 10px 24px rgba(0,0,0,0.25)",
                }}
                pt={{
                    body: { className: "p-2" }, // smaller card padding (reduces height a lot)
                    content: { className: "p-0" },
                }}
            >
                <div className="w-full flex flex-row flex-nowrap align-items-center gap-2">
                    {/* LEFT: Calendar */}
                    <FloatLabel className="flex-shrink-0" style={{ minWidth: "15rem" }}>
                        <Calendar
                            id="rma-date-range"
                            value={dates}
                            onChange={(e) => setDates(e.value)}
                            selectionMode="range"
                            readOnlyInput
                            dateFormat="dd/mm/yy"
                            hideOnRangeSelection
                            className="w-full"
                            inputClassName="p-inputtext-sm" // smaller input height
                        />
                        <label
                            className="bg-gray-300 text-white"
                            htmlFor="rma-date-range"
                        >
                            Date range
                        </label>
                    </FloatLabel>

                    {/* RIGHT: Filters + Print */}
                    <div className="flex flex-row flex-nowrap align-items-center gap-3 ml-auto flex-shrink-0">
                        <div className="flex flex-row flex-nowrap gap-3 align-items-center flex-shrink-0">
                            {FILTERS.map((f, idx) => (
                                <div key={f.key} className="flex align-items-center gap-2 flex-shrink-0">
                                    <Checkbox
                                        inputId={`rma-filter-${idx}`}
                                        value={f.key}
                                        onChange={toggleFilter}
                                        checked={selectedFilters.includes(f.key)}
                                    />
                                    <label htmlFor={`rma-filter-${idx}`} className="text-xs leading-none">
                                        {f.label}
                                    </label>
                                </div>
                            ))}
                        </div>

                        <Button
                            label="Print"
                            icon="pi pi-print"
                            className="p-button-success p-button-sm flex-shrink-0" // smaller button height
                            disabled={!Array.isArray(data) || data.length === 0}
                            onClick={handlePrint}
                        />
                    </div>
                </div>
            </Card>
        );
    };

    const RMASelect = () => {
        if (!Array.isArray(data) || loading || !Array.isArray(techList)) return null;

        return (
            <RMAGrid
                data={deliveredRows}
                setData={setData}
                setAction={handleAction}
                selectedDate={selectedDate}
                technician={techList}
            />
        );
    };

    if (loading) return <ToastContainer/>;

    if (!Array.isArray(data) || !Array.isArray(techList)) {
        return (
            <>
                <ToastContainer/>
                <div className="flex justify-content-center p-4">
                    <Card title="RMA" subTitle="Loading data…">
                        <div className="text-600">Please wait.</div>
                    </Card>
                </div>
            </>
        );
    }

    const toolbarStart = (
        <div className="flex align-items-center gap-2">
            <MunshiMenu handle={handle}/>
        </div>
    );

    const toolbarEnd = (
        <div className="flex flex-column align-items-end">
            <div className="text-xl md:text-2xl font-bold text-white">RMA</div>
            <div className="text-sm text-white">
                User: <span className="font-medium text-100">{user}</span>
                <span className="mx-2">•</span>
                DB: <span className="font-medium text-100">{database}</span>
            </div>
        </div>
    );

    return (
        <>
            <ToastContainer/>
            <div className="flex flex-column pl-2 pr-2 bg-gray-900 md:p-3">
                <Toolbar className="border-round-lg shadow-1" style={{background: '#71706E'}} start={toolbarStart} end={toolbarEnd}/>

                <div className="grid">
                    <div className="col-12">
                        <PrintTools/>
                    </div>
                    <div className="col-12">
                        <div className="flex justify-content-center w-full">
                            <RMASelect/>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};